import { FC, useEffect, useState } from "react";

import { Button, Dropdown, Table } from "react-bootstrap";
import "../../../assets/scss/DataGrid.scss";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { confirmDialog } from "primereact/confirmdialog";

import { AppointmentStatusSlug } from "@/_constants/appointment.ts";
import { ServiceOrderType } from "@/_constants/serviceOrder.ts";
import { SCvalues } from "@/_helpers/commonComponentsTypes.ts";
import { ButtonFile } from "@/common/components/ButtonFile.tsx";
import ColoredMarker from "@/common/components/ColoredMarker";
import Loader from "@/common/components/Loader";
import SelectCreatable from "@/common/components/SelectCreatable.tsx";
import UploadDocumentForm from "@/common/components/UploadDocumentForm";
import { WarningMessage } from "@/common/components/WarningMessage";
import { ExternalUrl } from "@/common/interfaces";
import { useGetFile } from "@/hooks/useGetFile.tsx";
import { useGetUserType } from "@/modules/auth/hooks/userType.hooks";
import ExternalFiles from "@/modules/order/components/ExternalFiles";
import { useUpdateManyServiceReportFiles } from "@/modules/order/hooks/serviceReportFile.hooks";
import {
  deleteFileServiceReport,
  regenerateServiceReportPDF,
  resendEmailReport as resendEmailReportRequest,
  updateServiceMainFile,
  uploadFilesService,
} from "@/modules/order/services";
import { ServiceReportFile } from "@/modules/serviceOrdering/interfaces/ServiceReportFile";
import { IFile } from "@/modules/serviceOrdering/reducers";
import { RootState } from "@/store";

export interface OrderFilesDataGridComponentProps {
  serviceId: string;
  files?: IFile[];
  serviceReportId?: string;
  refetch: () => void;
  mainFile: IFile;
  hasUpdatePermission: boolean;
  hasSuperAdminPermission: boolean;
  type: ServiceOrderType;
  appointmentStatusSlug: AppointmentStatusSlug;
  externalUrls?: ExternalUrl[];
}

const OrderFilesDataGridComponent: FC<OrderFilesDataGridComponentProps> = ({
  hasSuperAdminPermission,
  appointmentStatusSlug,
  hasUpdatePermission,
  serviceReportId,
  serviceId,
  mainFile,
  refetch,
  files,
  type,
  externalUrls,
}) => {
  const [newFiles, setNewFiles] = useState<Array<any>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const referentials = useSelector(
    (state: RootState) => state.referentialReducer.referentials
  );
  const [disableGenerateButton, setDisableGenerateButton] = useState(false);

  const allFilesLocked = files?.every(
    (file) => file?.serviceReportFile?.locked
  );

  const filesExist = Boolean(files?.length);

  const {
    mutate: updateManyServiceReportFiles,
    isPending: isPendingUpdateManyServiceReportFiles,
  } = useUpdateManyServiceReportFiles({
    handleSuccess: () => {
      toast.success(
        allFilesLocked
          ? "Documents déverrouillés avec succès"
          : "Documents verrouillés avec succès"
      );
      refetch();
    },
  });

  const { isSuperAdmin } = useGetUserType();

  const modifyServiceReportFiles = (locked: boolean) => {
    return files?.map((file) => {
      return {
        file_id: file?.id,
        service_report_id: serviceReportId,
        locked,
      };
    });
  };

  const handleModifyFiles = () => {
    const serviceReportFiles = modifyServiceReportFiles(!allFilesLocked);
    updateManyServiceReportFiles(serviceReportFiles as ServiceReportFile[]);
  };

  let selectableFiles: SCvalues[] = [];

  if (filesExist) {
    selectableFiles =
      files?.map((file) => {
        return {
          label: file.type.label,
          value: file.id!,
        };
      }) || [];
  }

  const {
    handleSubmit,
    control,
    formState: { dirtyFields, isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      mainFile: mainFile?.id,
    },
  });

  const [mainFilePath, setMainFilePath] = useState(mainFile?.path || undefined);
  const mainFileId = useWatch({ name: "mainFile", control: control });
  useEffect(() => {
    const mainFile = files?.find((item) => item.id === mainFileId);
    setMainFilePath(mainFile?.path);
  }, [mainFileId]);

  const fileHandler = async (file: string) => {
    window.open(file);
  };

  const { getFile } = useGetFile({
    enabled: false,
    listFiles: [mainFilePath!],
    keys: ["file", mainFilePath!],
  });

  const canRegenerate = () => {
    return (
      hasSuperAdminPermission &&
      mainFile &&
      (type === ServiceOrderType.OUTGOING_INVENTORY ||
        type === ServiceOrderType.OUTGOING_INCOMING_INVENTORY ||
        type === ServiceOrderType.INCOMING_INVENTORY)
    );
  };

  const canResendEmail = () => {
    return appointmentStatusSlug === AppointmentStatusSlug.DONE && mainFilePath;
  };

  const onSubmit = async (data: any) => {
    if (dirtyFields?.mainFile) {
      try {
        await updateServiceMainFile(serviceId, data.mainFile);
        toast.success("Fichier principal modifié avec succès");
        reset(data);
      } catch (e) {
        toast.error(
          "Impossible de modifier un fichier principal à cette commande.\nVeuillez contacter votre administrateur système."
        );
      }
    }
  };

  const handleUploadDocuments = async (documents: any[]) => {
    if (!serviceReportId || !hasUpdatePermission) {
      return;
    }
    try {
      setIsUploading(true);
      await uploadFilesService(serviceReportId, documents);
      toast.success("Fichiers sauvegardés.");
      refetch();
    } catch (e) {
      toast.error(
        "Impossible de sauvegarder vos fichiers.\nVeuillez contacter votre administrateur système."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const deleteFileHandler = async (fileId: string) => {
    if (!serviceReportId || !hasUpdatePermission) {
      return;
    }
    try {
      await deleteFileServiceReport(serviceReportId, fileId);
      setNewFiles([]);
      toast.success("Fichier supprimer.");
      refetch();
    } catch (e) {
      toast.error(
        "Impossible de supprimer votre fichier.\nVeuillez contacter votre administrateur système."
      );
    }
  };

  const regenerateReportHandler = async () => {
    if (!serviceReportId || !hasSuperAdminPermission) {
      return;
    }
    try {
      setDisableGenerateButton(true);
      toast.info(
        "Patientez pendant que votre rapport PDF se régénére ou se génére."
      );

      await regenerateServiceReportPDF(serviceReportId);

      toast.success("Rapport PDF régénérer ou générer avec succès.");
      setDisableGenerateButton(false);
      refetch();
    } catch (e) {
      setDisableGenerateButton(false);
      toast.error(
        "Impossible de régénérer ou générer votre Rapport PDF.\nVeuillez contacter votre administrateur système."
      );
    }
  };

  const resendEmailReport = async () => {
    if (!serviceId) {
      return;
    }
    try {
      await resendEmailReportRequest(serviceId);
      toast.success("Email d'intervention renvoyé.");
      refetch();
    } catch (e) {
      toast.error(
        "Impossible d'envoyé l'email d'intervention.\nVeuillez contacter votre administrateur système."
      );
    }
  };

  const deleteConfirm = (fileId: string) => {
    confirmDialog({
      message: `Voulez-vous supprimer ce fichier ?`,
      header: "Confirmation de suppression",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Oui",
      rejectLabel: "Non",
      accept: () => deleteFileHandler(fileId),
    });
  };

  const regenerateReportConfirm = () => {
    confirmDialog({
      message: `Voulez-vous vraiment régénérer ou générer le rapport ?`,
      header: "Confirmation de régénération/génération",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Oui",
      rejectLabel: "Non",
      accept: () => regenerateReportHandler(),
    });
  };

  const resendEmailReportConfirm = () => {
    confirmDialog({
      message: `Voulez-vous renvoyer l'email d'intervention ?`,
      header: "Confirmation de renvoie de l'email d'intervention",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Oui",
      rejectLabel: "Non",
      accept: () => resendEmailReport(),
    });
  };
  return (
    <>
      <div className="orderPage-preview d-flex flex-column gap-3">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex align-items-center">
            <label>Fichier principal :</label>
            <div
              className={
                "d-flex align-items-center justify-content-between w-100 gap-3"
              }
            >
              <div className={"d-flex w-100"}>
                <Controller
                  control={control}
                  name={"mainFile"}
                  render={({ field }) => {
                    return (
                      <SelectCreatable
                        className={"w-100"}
                        options={selectableFiles}
                        getValue={(data: { value: string; label: string }) =>
                          field.onChange(data.value)
                        }
                        isMulti={false}
                        creatable={false}
                        defaultValue={selectableFiles.find(
                          (item) => item.value === mainFile?.id
                        )}
                      />
                    );
                  }}
                />
              </div>
              {!isSubmitting && (
                <Button variant="primary" size="sm" type={"submit"}>
                  Sauvegarder
                </Button>
              )}
              {mainFilePath || canRegenerate() || canResendEmail() ? (
                <Dropdown>
                  <Dropdown.Toggle variant="success" id="dropdown-basic">
                    <div className="-mt-1 py-0.5 text-white font-extrabold">
                      ...
                    </div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {mainFilePath && !allFilesLocked && (
                      <Dropdown.Item onClick={() => getFile(fileHandler)}>
                        Télécharger
                      </Dropdown.Item>
                    )}
                    {isSuperAdmin &&
                      filesExist &&
                      appointmentStatusSlug === AppointmentStatusSlug.DONE && (
                        <Dropdown.Item onClick={handleModifyFiles}>
                          {allFilesLocked
                            ? "Déverrouiller les documents"
                            : "Verrouiller les documents"}
                        </Dropdown.Item>
                      )}
                    {canRegenerate() && (
                      <Dropdown.Item onClick={() => regenerateReportConfirm()}>
                        Régénéré ou généré le rapport
                      </Dropdown.Item>
                    )}
                    {canResendEmail() && (
                      <Dropdown.Item onClick={() => resendEmailReportConfirm()}>
                        Renvoyer l'email d'intervention
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              ) : null}
            </div>
          </div>
        </form>
      </div>
      <hr />
      <div>
        {filesExist &&
          allFilesLocked &&
          appointmentStatusSlug === AppointmentStatusSlug.DONE && (
            <WarningMessage
              containerClassName="justify-center mb-4"
              text="Les documents sont verrouillés"
            />
          )}
        <div className="relative">
          {filesExist ? (
            <Table borderless responsive className="">
              <tbody className="property-data-grid mt-3">
                {files?.map((file, index) => (
                  <tr className="datagrid-fields" key={index}>
                    <td className="text-center">
                      <ColoredMarker className="text-no-wrap" color="#000D5B">
                        {file.type.label}
                      </ColoredMarker>
                    </td>
                    <td className="text-center dark-purple-text">
                      <ButtonFile
                        path={file.path}
                        label="Voir le document"
                        isDisabled={file?.serviceReportFile?.locked}
                      />
                    </td>
                    {hasUpdatePermission && (
                      <td className="text-center">
                        <Button
                          className="btn-trash pt-[9px] flex justify-center items-center"
                          onClick={() => deleteConfirm(`${file.id}`)}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <WarningMessage
              containerClassName="justify-center mb-4"
              text="Aucun document"
            />
          )}
          {serviceReportId && hasUpdatePermission && (
            <div className="d-flex flex-column gap-3">
              <div className="d-flex flex-column gap-3">
                <UploadDocumentForm
                  documents={newFiles}
                  setDocuments={setNewFiles}
                  referentials={referentials}
                  onUpload={handleUploadDocuments}
                  isUploading={isUploading}
                  showUploadButton={true}
                />
              </div>
            </div>
          )}
          {isPendingUpdateManyServiceReportFiles && (
            <div className="absolute -top-3 left-0 w-full h-full flex justify-center items-center">
              <Loader />
            </div>
          )}
        </div>
        {serviceReportId && hasUpdatePermission && (
          <ExternalFiles
            serviceId={serviceId}
            externalUrls={externalUrls || []}
          />
        )}
      </div>
    </>
  );
};

export default OrderFilesDataGridComponent;
